import { FetchError } from '@chronosphereio/core';

export type FetchFn = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

/**
 * Calls `globalThis.fetch` and determines which type of error to show for non-200 responses.
 */
export async function fetch(...args: Parameters<typeof globalThis.fetch>) {
  const response = await globalThis.fetch(...args);

  if (response.ok === false) {
    let json;
    const clonedResponse = response.clone();

    try {
      json = await response.json();
    } catch {
      throw new FetchError(clonedResponse);
    }

    if (json?.error) {
      throw new UserFriendlyError(json.error);
    }
    if (json?.message) {
      throw new UserFriendlyError(json.message);
    }
  }

  return response;
}

/**
 * Calls `globalThis.fetch` and throws a `FetchError` on non-200 responses, but also
 * decodes the response body as JSON, casting it to type `T`. Returns the
 * decoded body.
 */
export async function fetchJson<T>(fetchFn: FetchFn = fetch, ...args: Parameters<typeof globalThis.fetch>) {
  const response = await fetchFn(...args);
  const json: T = await response.json();
  return json;
}

/**
 * General error type for an error that has a message that is OK to show to the end user.
 */
export class UserFriendlyError extends Error {
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, UserFriendlyError.prototype);
  }
}
